import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { A3iService } from 'app/services/a3i.service';
import { ActivityService } from 'app/services/activity.service';
import { AttrIndexService } from 'app/services/attr-index.service';
import { CommonFunctionService } from 'app/services/common-function.service';
import { TableColumn } from 'app/services/common-table.service';
import { DomainService, GivColumnDomain } from 'app/services/domain.service';
import { EditService } from 'app/services/edit.service';
import { Gto, GtoService } from 'app/services/gto.service';
import { LayerQcService, QcEditFilter } from 'app/services/layer-qc.service';
import { AppLayer } from 'app/services/layer.service';
import { MainDataService } from 'app/services/main-data.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-a3i-edit-card',
  templateUrl: './a3i-edit-card.component.html',
  styleUrls: ['../../content-edit/edit-card/edit-card.component.css', './a3i-edit-card.component.css']
})
export class A3iEditCardComponent implements OnInit, OnDestroy {

	@Input() disableEditing = false;

	columns: TableColumn[];
	selectedRecord;
	editRecordList: any[] = [];

	domainsByColumn: {
		[prop: string]: GivColumnDomain;
	} = {};

	isEditedCol: string;

	private selectedRecSub: Subscription;
	private columnsSub: Subscription;
	private commitSub: Subscription;

	constructor(
		private toastr: ToastrService,
		private activityService: ActivityService,
		private attrIndexService: AttrIndexService,
		private a3iService: A3iService,
		private commonService: CommonFunctionService,
		private domainService: DomainService,
		private editService: EditService,
		private gtoService: GtoService,
		private qcService: LayerQcService,
		private mainDataService: MainDataService
	) { }

	async onChange(col?: TableColumn) {
		if (!this.selectedRecord) {
			return;
		}
		if (!this.selectedRecord[this.isEditedCol]) {
			this.selectedRecord[this.isEditedCol] = true;
			this.editRecordList.push(this.selectedRecord);
		}
		if (this.a3iService.commitDisableBS.getValue().disabled) {
			this.a3iService.commitDisableBS.next({
				disabled: false
			});
		}
	}
	checkFieldDirty(col: TableColumn) {
		const originAttrs = this.mainDataService.getDataOriginAttrs(this.a3iService.getRecordId(this.selectedRecord));
		if (originAttrs) {
			return this.editService.hasValueChanged(this.selectedRecord[col.prop], originAttrs[col.prop]);
		}
	}
	goPrevRecord() {
		const idx = this.editRecordList.indexOf(this.selectedRecord);
		if (idx <= 0) {
			return;
		}
		this.changeSelectedRecord(this.editRecordList[idx - 1]);
	}
	goNextRecord() {
		const idx = this.editRecordList.indexOf(this.selectedRecord);
		if (idx >= this.editRecordList.length - 1) {
			return;
		}
		this.changeSelectedRecord(this.editRecordList[idx + 1]);
	}
	clear() {
		this.editRecordList = [];
		this.selectedRecord = null;
		if (!this.a3iService.commitDisableBS.getValue().disabled) {
			this.a3iService.commitDisableBS.next({
				disabled: true
			});
		}
	}

	private changeSelectedRecord(rec, skipTrigger?: boolean) {
		if (rec) {
			this.selectedRecord = rec;
		} else {
			this.selectedRecord = null;
		}
		if (!skipTrigger) {
			this.a3iService.selectedRecordA3iBS.next({
				record: this.selectedRecord,
				source: 'card'
			});
		}
	}

	private async commit() {
		const spinnerId = this.commonService.getRandomId();
		this.commonService.setLoadingSpinner(true, spinnerId);
		try {
			let originList = this.editRecordList.map(i => {
				return this.mainDataService.getDataOriginAttrs(this.a3iService.getRecordId(i));
				// return i[this.attrIndexService.originalDataKey]
			});
			const data = await this.handleEditedRecordList(this.editRecordList, originList);
			const gtos = data.gtos;
			if (gtos.length) {
				console.log(gtos);
				try {
					await this.gtoService.processGto(gtos);	
					this.toastr.success('Successfully updated.');
				} catch (err) {
					this.toastr.error('Update failed.');
					console.log(err);
					this.commonService.setLoadingSpinner(false, spinnerId);
					return;
				}
				// Dynamic Qc
				try {
					const layer = this.activityService.selectedLayerBS.getValue().layer;
					const dynamicQcKeyvals = data.dynamicQcKeyvals;
					const editFilters: QcEditFilter[] = [{
						gly_id: layer.id,
						cols: data.changedCols,
						keyvals: dynamicQcKeyvals
					}];
					if (data.changedCols.length) {
						const rsp = await this.qcService.qcLayersDynamic(editFilters);
						if (rsp.misc  && rsp.misc.transition > 0) {
							this.qcService.qcLayers([layer.id]);
						}
					}	
				} catch (err) {
					console.log(err);
				}
				this.a3iService.commitSubject.next({
					state: 'done'
				});
			}
		} catch (err) {
			console.log(err);
		}
		this.commonService.setLoadingSpinner(false, spinnerId);
	}
	private async handleEditedRecordList(list: any[], originList: any[]) {
		if (!list.length) {
			return;
		}
		const layer = this.activityService.selectedLayerBS.getValue().layer;
		// Get sourceTableName
		const sourceTableName = layer.category.tableName;

		let changedCols: string[] = [];
		//
		let updateGtos: Gto[];
		let gtos: Gto[] = [];
		const updateRecords = [];
		const updateRecordsOrigin = [];
		list.forEach(r => {
			updateRecords.push(r);
			const origin = originList.find(o => this.a3iService.getRecordId(o) === this.a3iService.getRecordId(r));
			updateRecordsOrigin.push(origin);
		});
		const prefix = layer.category.colPrefix;
		let dynamicQcKeyvals;
		let cols: string[] = this.editService.getUpdateColsInSourceTable(sourceTableName, prefix);
		const primKeyCol = this.editService.getPrimKeyCol(prefix);

		if (updateRecords.length) {
			dynamicQcKeyvals = updateRecords.map(r => this.a3iService.getRecordId(r));
			const updateGtosInfo = this.editService.getUpdateGtosByRecordsArchive(cols, updateRecords, updateRecordsOrigin, sourceTableName, prefix, primKeyCol, this.attrIndexService.geomColManual);
			updateGtos = updateGtosInfo.gtos;
			changedCols = updateGtosInfo.changedCols;
			gtos = gtos.concat(updateGtos);
		}
		changedCols = changedCols.filter(c => c);
		return {
			gtos: gtos,
			changedCols: changedCols,
			dynamicQcKeyvals: dynamicQcKeyvals
		};
	}

	ngOnInit() {
		this.isEditedCol = this.attrIndexService.isEditedKey;

		this.selectedRecSub = this.a3iService.selectedRecordA3iBS.subscribe(data => {
			if (data.source !== 'card') {
				this.changeSelectedRecord(data.record, true);
			}
		});
		this.columnsSub = this.a3iService.a3iEditColumnsBS.subscribe(data => {
			this.columns = data.columns;
			const layer = this.activityService.selectedLayerBS.getValue().layer;
			const colsForDomain = this.columns.map(c => {
				return {
					prop: c.prop,
					standard: layer.category.standardCols.find(s => s.colName.toLowerCase() === c.prop.substring(4))
				}
			})
			this.domainsByColumn = this.domainService.getColumnDomains(colsForDomain, layer.category.code, this.selectedRecord);
		});
		this.commitSub = this.a3iService.commitSubject.subscribe(async data => {
			if (data.state === 'start') {
				await this.commit();
			}
		});
	}
	ngOnDestroy(): void {
		this.selectedRecSub.unsubscribe();
		this.columnsSub.unsubscribe();
		this.commitSub.unsubscribe();
	}
}
